import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  Paper,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import {
  Close as CloseIcon,
  Visibility as VisibilityIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';

const ProcessingResultsDialog = ({ open, onClose, processingData, loading }) => {
  const [jsonDialogOpen, setJsonDialogOpen] = React.useState(false);
  const [jsonData, setJsonData] = React.useState('');

  const handleViewJson = () => {
    setJsonData(JSON.stringify(processingData, null, 2));
    setJsonDialogOpen(true);
  };

  const processingResultsColumns = [
    {
      field: 'step',
      headerName: 'Processing Step',
      flex: 1.5,
      minWidth: 180,
      renderCell: (params) => (
        <Typography noWrap sx={{ maxWidth: 180 }}>
          {params.value || 'N/A'}
        </Typography>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 1,
      minWidth: 120,
      renderCell: (params) => {
        const status = params.row.status || 'Unknown';
        let color = 'default';
        let icon = null;
        
        if (status === 'completed' || status === 'success') {
          color = 'success';
        } else if (status === 'failed' || status === 'error') {
          color = 'error';
        } else if (status === 'warning') {
          color = 'warning';
        }
        
        return (
          <Chip 
            label={status} 
            size="small" 
            color={color}
            variant="outlined"
          />
        );
      },
    },
    {
      field: 'timestamp',
      headerName: 'Timestamp',
      flex: 1.5,
      minWidth: 160,
      valueFormatter: (params) => params.value ? new Date(params.value).toLocaleString() : 'N/A',
    },
    {
      field: 'message',
      headerName: 'Message',
      flex: 2,
      minWidth: 200,
      renderCell: (params) => (
        <Tooltip title={params.value || 'No message'}>
          <Typography noWrap sx={{ maxWidth: 200 }}>
            {params.value || 'No message'}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: 'output',
      headerName: 'Output',
      flex: 2,
      minWidth: 200,
      renderCell: (params) => (
        <Tooltip title={params.value || 'No output'}>
          <Typography noWrap sx={{ maxWidth: 200 }}>
            {params.value || 'No output'}
          </Typography>
        </Tooltip>
      ),
    },
  ];

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            height: '80vh',
            maxHeight: '80vh',
          },
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          pb: 1
        }}>
          <Typography variant="h6" component="div">
            Processing Results
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {processingData && processingData.length > 0 && (
              <Button
                size="small"
                onClick={handleViewJson}
                startIcon={<VisibilityIcon />}
              >
                View JSON
              </Button>
            )}
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0, height: 'calc(80vh - 64px)', overflow: 'hidden' }}>
          <Paper sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ 
              p: 2, 
              borderBottom: '1px solid #e0e0e0', 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              bgcolor: '#f8f9fa'
            }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Processing Results
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {processingData ? `${processingData.length} records` : '0 records'}
              </Typography>
            </Box>
            <Box sx={{ flex: 1, overflow: 'hidden' }}>
              {loading ? (
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  alignItems: 'center', 
                  height: '100%',
                  color: 'text.secondary'
                }}>
                  <Typography>Loading processing results...</Typography>
                </Box>
              ) : processingData && processingData.length > 0 ? (
                <DataGridPro
                  rows={Array.isArray(processingData) ? processingData : []}
                  columns={processingResultsColumns}
                  columnResizeMode="onEnd"
                  resizable
                  pagination
                  rowsPerPageOptions={[10, 25, 50]}
                  pageSize={25}
                  sx={{
                    '& .MuiDataGrid-root': {
                      border: 'none',
                    },
                    '& .MuiDataGrid-columnHeaders': {
                      backgroundColor: '#f8f9fa',
                      fontWeight: 600,
                    },
                  }}
                  disableRowSelectionOnClick
                />
              ) : (
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  alignItems: 'center', 
                  height: '100%',
                  color: 'text.secondary'
                }}>
                  <Typography>No processing results available</Typography>
                </Box>
              )}
            </Box>
          </Paper>
        </DialogContent>
      </Dialog>

      {/* JSON View Dialog */}
      <Dialog
        open={jsonDialogOpen}
        onClose={() => setJsonDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>JSON Data</DialogTitle>
        <DialogContent>
          <Box sx={{ 
            bgcolor: '#f5f5f5', 
            p: 2, 
            borderRadius: 1,
            fontFamily: 'monospace',
            fontSize: '0.875rem',
            maxHeight: '400px',
            overflow: 'auto',
            whiteSpace: 'pre-wrap'
          }}>
            {jsonData || 'No JSON data available'}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setJsonDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ProcessingResultsDialog;
